import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Download, XCircle, Loader2, ArrowUpRight } from "lucide-react";

export default function OrderSuccessPage() {
    const [params] = useSearchParams();
    const orderId = params.get("order_id");
    const [order, setOrder] = useState(null);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        if (!orderId) { setStatus("error"); return; }
        let tries = 0;
        const poll = async () => {
            tries += 1;
            try {
                const { data } = await api.get(`/orders/${orderId}`);
                setOrder(data);
                if (data.status === "paid") setStatus("paid");
                else if (data.status === "failed") setStatus("failed");
                else if (tries < 8) setTimeout(poll, 1500);
                else setStatus("pending");
            } catch (e) { setStatus("error"); }
        };
        poll();
    }, [orderId]);

    return (
        <div data-testid="order-success-page" className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16">
            <div className="w-full max-w-lg text-center">
                {status === "loading" && (
                    <>
                        <Loader2 className="mx-auto text-volt animate-spin" size={40} />
                        <div className="mt-6 font-heading text-2xl uppercase">Confirming payment…</div>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle2 className="mx-auto text-volt" size={48} />
                        <h1 className="mt-6 font-heading text-4xl md:text-5xl uppercase">Payment Successful</h1>
                        <p className="mt-4 text-white/70">
                            Thank you, {order?.first_name}! Your order for <span className="text-volt">{order?.program_title}</span> is confirmed.
                        </p>
                        {order?.pdf_url && (
                            <a href={order.pdf_url} target="_blank" rel="noreferrer"
                               data-testid="download-pdf-link"
                               className="btn-volt px-8 py-4 mt-8 inline-flex items-center gap-2">
                                <Download size={16} /> Download Program PDF
                            </a>
                        )}
                        <p className="mt-6 text-xs text-white/50">
                            A copy has also been sent to <span className="text-white/80">{order?.email}</span>.
                        </p>

                        {/* UPSELL: 15% off personal Frankfurt session */}
                        <div data-testid="upsell-card" className="mt-14 border border-volt/40 bg-gradient-to-br from-volt/10 via-transparent to-volt/5 p-8 text-left relative overflow-hidden">
                            <div className="absolute top-3 right-3 text-[10px] font-heading tracking-widest text-black bg-volt px-2 py-1">
                                -15% · LIMITED
                            </div>
                            <div className="font-heading text-volt tracking-widest text-xs mb-3">
                                EXCLUSIVE OFFER · MEMBERS ONLY
                            </div>
                            <div className="font-heading text-2xl md:text-3xl uppercase leading-tight">
                                Book a personal session<br />
                                in Frankfurt — <span className="text-volt">save 15%</span>
                            </div>
                            <p className="mt-4 text-sm text-white/70">
                                Because you purchased the Ball Mastery program, we're offering you an in-person 1-on-1
                                session with our head coach at 15% off. Just mention the code below when booking.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-4">
                                <div className="border border-dashed border-volt px-4 py-2.5 font-heading text-volt tracking-widest text-lg">
                                    EPA-BM15
                                </div>
                                <Link to="/trainings" data-testid="upsell-cta"
                                      className="btn-volt px-6 py-3 text-sm inline-flex items-center gap-2">
                                    Book Session <ArrowUpRight size={14} />
                                </Link>
                            </div>
                            <p className="mt-3 text-[10px] text-white/40 uppercase tracking-widest">
                                Valid 14 days · New students · Cannot be combined with other offers
                            </p>
                        </div>
                    </>
                )}
                {status === "pending" && (
                    <>
                        <Loader2 className="mx-auto text-white/60 animate-spin" size={40} />
                        <div className="mt-6 font-heading text-2xl uppercase">Payment pending</div>
                        <p className="mt-3 text-white/60 text-sm">
                            Your payment is still processing. Refresh this page in a moment.
                        </p>
                    </>
                )}
                {(status === "failed" || status === "error") && (
                    <>
                        <XCircle className="mx-auto text-red-400" size={40} />
                        <div className="mt-6 font-heading text-2xl uppercase">Something went wrong</div>
                        <p className="mt-3 text-white/60 text-sm">
                            No charge was made. Please try again from the program page.
                        </p>
                    </>
                )}
                <Link to="/programs" className="mt-10 inline-block text-xs uppercase tracking-widest text-white/60 hover:text-volt">
                    ← Back to Programs
                </Link>
            </div>
        </div>
    );
}
